import { useEffect, useState } from 'react';
import {
  GUARANTEED_JEV_TRIP_MIN_HITS_DEFAULT,
  GUARANTEED_JEV_TRIP_WINDOW_SECONDS_DEFAULT,
  isGuaranteedJevMode,
  isGuaranteedJevTripMinHits,
  isGuaranteedJevTripWindowSeconds,
  isGuaranteedMode,
  isGuaranteedProbeShape,
  type GuaranteedMode,
  type GuaranteedProbeShape,
  type GuaranteedServiceSkuEntry,
  type PatchGuaranteedServiceRequest,
} from '@nadoutong/contracts';
import { SlidersHorizontal, Trash2 } from 'lucide-react';
import { useT } from '../../i18n/index.ts';
import { Button, cx, IconButton, Input, Select } from '../../ui/index.ts';
import { jevSkipLine } from './jevSkips.ts';
import { ModeControl } from './ModeControl.tsx';
import { GuardConfigCell } from './guard/GuardConfigCell.tsx';
import type { GuardConfigRow } from '../../api/guaranteedGuardConfig.ts';
import { overlayFieldCount } from './guard/guardModel.ts';
import { fallbackLabelKey, modeLabelKey, skuChainMode, skuFallbackMode, skuProbeMode } from './modes.ts';
import { probeSummary } from './probeCopy.ts';
import { probeValuesOf } from './probeSettingsModel.ts';
import { ProbeSettingsDrawer } from './ProbeSettingsDrawer.tsx';
import { RowWriteStatus } from './RowWriteStatus.tsx';
import { CommitNumber, RowStats, SKU_GRID, SkuName } from './SkuRowParts.tsx';
import type { PatchOptions, WriteOutcome } from './useGuaranteedWrites.ts';
import { isRowBusy, pendingField, type RowWriteStatus as RowStatus } from './writeStatus.ts';

function shownInt(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : fallback;
}

/** The pending target while this row is saving, else the stored value. */
function shownMode<T extends string>(status: RowStatus | undefined, field: string, stored: T, guard: (value: unknown) => value is T): T {
  const pending = pendingField(status, field);
  return guard(pending) ? pending : stored;
}

function shownNumber(status: RowStatus | undefined, field: string, stored: number): number {
  const pending = pendingField(status, field);
  return typeof pending === 'number' && Number.isSafeInteger(pending) ? pending : stored;
}

export function SkuConfigCard({
  row,
  status,
  skuGuard,
  orgGuards,
  onPatch,
  onEditNote,
  onDelete,
  onOpenGuard,
  guardDisabled = false,
}: {
  readonly row: GuaranteedServiceSkuEntry;
  /** This row's write state (#2658); every control is disabled while saving. */
  readonly status?: RowStatus;
  readonly skuGuard: GuardConfigRow | undefined;
  readonly orgGuards: readonly GuardConfigRow[];
  readonly onPatch: (body: PatchGuaranteedServiceRequest, options?: PatchOptions) => Promise<WriteOutcome>;
  readonly onEditNote: () => void;
  readonly onDelete: () => void;
  readonly onOpenGuard: () => void;
  readonly guardDisabled?: boolean;
}): JSX.Element {
  const t = useT();
  const busy = isRowBusy(status);
  const probeMode = shownMode(status, 'probe_mode', skuProbeMode(row), isGuaranteedMode);
  const chainMode = shownMode(status, 'chain_trip_mode', skuChainMode(row), isGuaranteedMode);
  const fallbackMode = shownMode(status, 'ultimate_fallback', skuFallbackMode(row), isGuaranteedMode);
  const probesOff = probeMode === 'off';
  const summary = probeSummary(probeValuesOf(row), t);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const modeText = (mode: GuaranteedMode): string => t(modeLabelKey(mode));
  const fallbackText = (mode: GuaranteedMode): string => t(fallbackLabelKey(mode));
  const jev = shownMode(status, 'jev_judge', isGuaranteedJevMode(row.jev_judge) ? row.jev_judge : 'off', isGuaranteedJevMode);
  // Only while Jev is on: skips are counted for SKUs with Jev on (#2658).
  const jevSkips = jev === 'off' ? null : jevSkipLine(row.jev_skips_1h, t);
  const rowShape = isGuaranteedProbeShape(row.probe_shape) ? row.probe_shape : 'ping';
  const [shape, setShape] = useState<GuaranteedProbeShape>(rowShape);
  const [customText, setCustomText] = useState(row.probe_custom_text ?? '');
  const [customMax, setCustomMax] = useState(
    row.probe_custom_max_tokens === null || row.probe_custom_max_tokens === undefined
      ? ''
      : String(row.probe_custom_max_tokens),
  );
  const [customError, setCustomError] = useState<string | null>(null);
  function resetDrafts(): void {
    setShape(rowShape);
    setCustomText(row.probe_custom_text ?? '');
    setCustomMax(
      row.probe_custom_max_tokens === null || row.probe_custom_max_tokens === undefined
        ? ''
        : String(row.probe_custom_max_tokens),
    );
  }
  useEffect(() => {
    setShape(rowShape);
    setCustomText(row.probe_custom_text ?? '');
    setCustomMax(
      row.probe_custom_max_tokens === null || row.probe_custom_max_tokens === undefined
        ? ''
        : String(row.probe_custom_max_tokens),
    );
  }, [rowShape, row.probe_custom_text, row.probe_custom_max_tokens]);

  /** A refused write must not leave a local draft on screen (#2658). */
  function patchOrReset(body: PatchGuaranteedServiceRequest): void {
    void onPatch(body).then((outcome) => {
      if (!outcome.ok) resetDrafts();
    });
  }

  function saveCustom(): void {
    const text = customText;
    if (text.trim().length === 0 || /[\u0000-\u001F\u007F-\u009F]/.test(text)) {
      setCustomError(text.trim().length === 0
        ? t('guaranteed.probe_custom_empty')
        : t('guaranteed.probe_custom_invalid'));
      return;
    }
    if (Array.from(text.trim()).length > 200) {
      setCustomError(t('guaranteed.probe_custom_invalid'));
      return;
    }
    const maxRaw = customMax.trim();
    let max: number | null = null;
    if (maxRaw.length > 0) {
      const parsed = Number(maxRaw);
      if (!Number.isSafeInteger(parsed) || parsed < 1 || parsed > 64) {
        setCustomError(t('guaranteed.probe_custom_max_invalid'));
        return;
      }
      max = parsed;
    }
    setCustomError(null);
    const trimmed = text.trim();
    if (
      row.probe_shape === 'custom'
      && trimmed === (row.probe_custom_text ?? '')
      && max === (row.probe_custom_max_tokens ?? null)
    ) return;
    patchOrReset({
      probe_shape: 'custom',
      probe_custom_text: trimmed,
      probe_custom_max_tokens: max,
    });
  }

  const shapeOptions = [
    { value: 'ping', label: t('guaranteed.probe_shape_ping') },
    { value: 'arithmetic', label: t('guaranteed.probe_shape_arithmetic') },
    { value: 'echo', label: t('guaranteed.probe_shape_echo') },
    { value: 'custom', label: t('guaranteed.probe_shape_custom') },
  ];

  const hits = shownNumber(status, 'jev_trip_min_hits', shownInt(row.jev_trip_min_hits, GUARANTEED_JEV_TRIP_MIN_HITS_DEFAULT));
  const windowSeconds = shownNumber(
    status,
    'jev_trip_window_seconds',
    shownInt(row.jev_trip_window_seconds, GUARANTEED_JEV_TRIP_WINDOW_SECONDS_DEFAULT),
  );

  const nameCell = (
    <div className="min-w-0 pt-1">
      <SkuName name={row.sku_name} />
      <button
        type="button"
        className="mt-0.5 block max-w-full truncate text-left text-label text-paper-low hover:text-paper-mid"
        title={row.note ?? ''}
        data-testid={`guaranteed-note-${row.id}`}
        disabled={busy}
        onClick={onEditNote}
      >
        {row.note ?? t('guaranteed.note_placeholder')}
      </button>
    </div>
  );

  const probeControl = (
    <ModeControl
      value={probeMode}
      labelOf={modeText}
      disabled={busy}
      ariaLabel={t('guaranteed.probe.mode')}
      testId={`guaranteed-probe-enabled-${row.sku_id}`}
      onChange={(mode) => void onPatch({ probe_mode: mode })}
    />
  );

  const shapeControl = (
    <span className="block w-[6.75rem]">
      <Select
        compact
        value={shape}
        disabled={probesOff || busy}
        onValueChange={(value) => {
          if (!isGuaranteedProbeShape(value) || value === shape) return;
          setShape(value);
          setCustomError(null);
          if (value === 'custom') return;
          patchOrReset({
            probe_shape: value,
            probe_custom_text: null,
            probe_custom_max_tokens: null,
          });
        }}
        options={shapeOptions}
        ariaLabel={t('guaranteed.probe_shape')}
        testId={`guaranteed-probe-shape-${row.sku_id}`}
      />
    </span>
  );

  // Folds to its icon below 1536 px so the probe column fits a 1280 px window.
  const settingsButton = (
    <Button
      variant="ghost"
      size="sm"
      className="shrink-0"
      disabled={busy}
      onClick={() => setSettingsOpen(true)}
      aria-label={t('guaranteed.probe_settings.open')}
      title={t('guaranteed.probe_settings.open')}
      data-testid={`guaranteed-probe-settings-open-${row.sku_id}`}
    >
      <SlidersHorizontal aria-hidden="true" className="h-3.5 w-3.5" />
      <span className="hidden 2xl:inline">{t('guaranteed.probe_settings.open')}</span>
    </Button>
  );

  const summaryLine = (
    <span
      className="block min-w-0 text-label tabular-nums text-paper-low"
      title={summary}
      data-testid={`guaranteed-probe-summary-${row.sku_id}`}
    >
      {summary}
    </span>
  );

  const customBlock = shape === 'custom' ? (
    <div className="flex flex-col gap-1.5">
      <span className="text-label text-paper-low">{t('guaranteed.probe_custom_text')}</span>
      <Input
        compact
        value={customText}
        disabled={probesOff || busy}
        onChange={(event) => {
          setCustomText(event.currentTarget.value);
          setCustomError(null);
        }}
        onBlur={() => saveCustom()}
        placeholder={t('guaranteed.probe_custom_text')}
        aria-label={t('guaranteed.probe_custom_text')}
        invalid={customError !== null}
        data-testid={`guaranteed-probe-custom-text-${row.sku_id}`}
      />
      <span className="text-label text-paper-low">{t('guaranteed.probe_custom_max')}</span>
      <Input
        compact
        value={customMax}
        disabled={probesOff || busy}
        onChange={(event) => setCustomMax(event.currentTarget.value)}
        onBlur={() => saveCustom()}
        inputMode="numeric"
        aria-label={t('guaranteed.probe_custom_max')}
        placeholder="16"
        wrapperClassName="w-20"
        data-testid={`guaranteed-probe-custom-max-${row.sku_id}`}
      />
      {customError !== null ? (
        <span className="text-dense text-bad" data-testid={`guaranteed-probe-custom-error-${row.sku_id}`}>
          {customError}
        </span>
      ) : null}
    </div>
  ) : null;

  const chainControl = (
    <ModeControl
      value={chainMode}
      labelOf={modeText}
      disabled={busy}
      ariaLabel={t('guaranteed.chain_mode')}
      testId={`guaranteed-chain-trip-${row.sku_id}`}
      onChange={(mode) => void onPatch({ chain_trip_mode: mode })}
    />
  );

  const fallbackControl = (
    <ModeControl
      value={fallbackMode}
      labelOf={fallbackText}
      disabled={busy}
      ariaLabel={t('guaranteed.fallback_mode')}
      testId={`guaranteed-fallback-${row.id}`}
      onChange={(mode) => void onPatch({ ultimate_fallback: mode })}
    />
  );

  const jevControl = (
    <ModeControl
      value={jev}
      labelOf={modeText}
      disabled={busy}
      ariaLabel={t('guaranteed.jev.mode')}
      testId={`guaranteed-jev-${row.id}`}
      onChange={(mode) => void onPatch({ jev_judge: mode })}
    />
  );

  const hitsInput = (
    <CommitNumber
      value={hits}
      disabled={jev === 'off' || busy}
      testId={`guaranteed-jev-min-hits-${row.sku_id}`}
      label={t('guaranteed.jev_trip_min_hits')}
      validate={isGuaranteedJevTripMinHits}
      onCommit={(parsed) => void onPatch({ jev_trip_min_hits: parsed })}
    />
  );
  const windowInput = (
    <CommitNumber
      value={windowSeconds}
      disabled={jev === 'off' || busy}
      testId={`guaranteed-jev-window-${row.sku_id}`}
      label={t('guaranteed.jev_trip_window_seconds')}
      validate={isGuaranteedJevTripWindowSeconds}
      onCommit={(parsed) => void onPatch({ jev_trip_window_seconds: parsed })}
    />
  );
  // One line, dimmed while Jev is off.
  const jevCriteria = (
    <div className={cx('flex flex-nowrap items-center gap-1 whitespace-nowrap text-label text-paper-low', jev === 'off' && 'opacity-60')}>
      <span>{t('guaranteed.jev.criteria')}</span>
      {hitsInput}
      <span>{t('guaranteed.jev.hits')}</span>
      <span aria-hidden="true">/</span>
      {windowInput}
      <span>{t('guaranteed.jev.seconds')}</span>
    </div>
  );

  const jevNotes = (
    <>
      {jev === 'act' && probeMode !== 'act' ? (
        <p className="m-0 max-w-[16rem] text-micro text-paper-low" data-testid={`guaranteed-jev-expiry-only-${row.id}`}>
          {t('guaranteed.jev.expiry_only_hint')}
        </p>
      ) : null}
      {jevSkips !== null ? (
        <p
          className="m-0 max-w-[16rem] text-micro text-paper-low"
          title={t('guaranteed.jev.skips_hint')}
          data-testid={`guaranteed-jev-skips-${row.sku_id}`}
        >
          {jevSkips}
        </p>
      ) : null}
    </>
  );

  const deleteButton = (
    <IconButton
      label={t('guaranteed.delete')}
      size="sm"
      className="hover:text-bad"
      data-testid={`guaranteed-delete-${row.id}`}
      disabled={busy}
      onClick={onDelete}
    >
      <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
    </IconButton>
  );

  return (
    <div
      className={`${SKU_GRID} items-start border-b border-line-soft px-3 py-2.5`}
      data-testid={`guaranteed-row-${row.id}`}
      aria-busy={busy || undefined}
    >
      {nameCell}
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-1.5">
          {probeControl}
          {shapeControl}
          {settingsButton}
        </div>
        {summaryLine}
        {customBlock}
      </div>
      <div>{chainControl}</div>
      <div className="flex min-w-0 flex-col gap-1.5">
        {jevControl}
        {jevCriteria}
        {jevNotes}
      </div>
      <div>{fallbackControl}</div>
      <GuardConfigCell
        skuId={row.sku_id}
        skuPolicy={skuGuard?.policy ?? { v: 2 }}
        legacyStreamCutoffMs={typeof row.no_response_cutoff_ms === 'number' ? row.no_response_cutoff_ms : null}
        overlayCount={orgGuards.filter((entry) => overlayFieldCount(entry.policy) > 0).length}
        disabled={busy || guardDisabled}
        onOpen={onOpenGuard}
      />
      <RowStats row={row} />
      <div className="pt-0.5">{deleteButton}</div>
      {settingsOpen ? (
        <ProbeSettingsDrawer
          row={row}
          status={status}
          probesOff={probesOff}
          onClose={() => setSettingsOpen(false)}
          onPatch={(body) => onPatch(body, { quiet: true })}
        />
      ) : null}
      {status !== undefined ? (
        <div className="col-span-full pt-1.5">
          <RowWriteStatus status={status} testId={`guaranteed-write-status-${row.id}`} />
        </div>
      ) : null}
    </div>
  );
}
